import { useCallback, useEffect, useState } from 'react';

import { VisibilityState } from '@tanstack/react-table';

import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';

interface UseColumnConfigurationReturn {
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void;
  resetToDefaults: () => void;
}

// Default visibility for standard columns
const DEFAULT_VISIBILITY: VisibilityState = {
  // Checked by default
  type: true,
  'file.extension': true,
  issues: true,
  persons: true,
  date: true,

  // Unchecked by default
  author: false,
  factsCount: false,
  'file.createdDate': false,
};

const getStorageKey = (matterId: string) => `wexler_subset_columns_${matterId}`;

/**
 * Custom hook to manage column visibility configuration with localStorage persistence
 * per matter for the subset creator doc-picker-table.
 *
 * @param matterId - The matter ID to scope the configuration
 * @param relativityFields - Optional array of Relativity fields to include in defaults (hidden by default)
 * @param wexlerFields - Optional array of Wexler metadata fields to include in defaults (hidden by default)
 * @returns Column visibility state and setter functions
 */
export function useColumnConfiguration(
  matterId: string,
  relativityFields?: FilterField[],
  wexlerFields?: FilterField[],
): UseColumnConfigurationReturn {
  const [columnVisibility, setColumnVisibilityState] = useState<VisibilityState>(() => {
    let visibility = { ...DEFAULT_VISIBILITY };

    // Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(getStorageKey(matterId));
      if (stored) {
        visibility = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load column configuration from localStorage:', error);
    }

    // Add any new Wexler fields that aren't in stored state (default to hidden)
    if (wexlerFields) {
      for (const field of wexlerFields) {
        const key = `file.fileMetadata.wexlerFields.${field.fieldName}`;
        if (!(key in visibility)) {
          visibility[key] = false;
        }
      }
    }

    // Add any new Relativity fields that aren't in stored state (default to hidden)
    if (relativityFields) {
      for (const field of relativityFields) {
        const key = `file.fileMetadata.relativityFields.${field.fieldName}`;
        if (!(key in visibility)) {
          visibility[key] = false;
        }
      }
    }

    return visibility;
  });

  // Persist to localStorage whenever visibility changes
  useEffect(() => {
    try {
      localStorage.setItem(getStorageKey(matterId), JSON.stringify(columnVisibility));
    } catch (error) {
      console.error('Failed to save column configuration to localStorage:', error);
    }
  }, [matterId, columnVisibility]);

  const setColumnVisibility = useCallback(
    (visibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      if (typeof visibility === 'function') {
        setColumnVisibilityState((prev) => visibility(prev));
      } else {
        setColumnVisibilityState(visibility);
      }
    },
    [],
  );

  const resetToDefaults = useCallback(() => {
    setColumnVisibilityState(DEFAULT_VISIBILITY);
  }, []);

  return {
    columnVisibility,
    setColumnVisibility,
    resetToDefaults,
  };
}
